import type { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";

import { IS_DEV } from "@/data/constants";
import { TCustomerIOTraits } from "./types";

type TEventContextReader = () => TCustomerIOTraits;

type TInitOptions = {
  getEventContext: TEventContextReader;
};

const writeKey = import.meta.env.VITE_CUSTOMERIO_WRITE_KEY;

// EU workspaces only; unset falls back to the SDK's US defaults
const cdnURL = import.meta.env.VITE_CUSTOMERIO_CDN_URL;
const apiHost = import.meta.env.VITE_CUSTOMERIO_API_HOST;

// Null until load() resolves, so events are dropped rather than queued
let _client: AnalyticsBrowser | null = null;

// Injected once by initializeCustomerIO, alongside the client
let _getEventContext: TEventContextReader | undefined;

export const getCustomerIOClient = () => _client;

/** Identity and plan props every website event carries. */
export const getCustomerIOEventContext = (): TCustomerIOTraits => {
  if (!_getEventContext) return {};

  try {
    return _getEventContext();
  } catch {
    return {};
  }
};

const loadClient = async () => {
  try {
    // Imported lazily to keep the SDK out of the eagerly-loaded entry graph
    const { AnalyticsBrowser } =
      await import("@customerio/cdp-analytics-browser");

    const client = AnalyticsBrowser.load(
      { writeKey, ...(cdnURL ? { cdnURL } : {}) },
      {
        // Page views come from usePageTracking instead, for a consistent shape
        initialPageview: false,
        ...(apiHost
          ? { integrations: { "Customer.io Data Pipelines": { apiHost } } }
          : {}),
      },
    );

    await client;
    _client = client;
  } catch (error) {
    // Prevent Customer.io initialization errors from breaking application startup
    console.error("Failed to initialize CUSTOMER.IO:", error);
  }
};

export const initializeCustomerIO = (options: TInitOptions) => {
  if (_getEventContext) return;

  _getEventContext = options.getEventContext;

  if (IS_DEV) {
    console.info("Disabling CUSTOMER.IO in development environment.");
    return;
  }

  if (!writeKey) {
    console.warn("Customer.io write key is not set in environment variables.");
    return;
  }

  loadClient();
};
